/**
 * The thread header — task 5 (Tossker repaint), README §4 "Thread pane" and
 * §8 "Ownership states" (frame B8). 36px avatar · name (Montserrat 16/600) ·
 * tier badge (a button, with an icon and — Unresolved only — a dashed gold
 * border) · "Unclaimed" pill · meta line (`building-2` + channel icon) ·
 * peer pill · "Client says done" pill; right: ticket chip (gold) or dashed
 * "Attach ticket", Send a code, Hand off, a single primary **Resolve**.
 *
 * THE CLOSE VERB (task-5 brief binding decision 2). The old
 * `closeButtonLabel()` switched both the label ("Close"/"Dismiss") and the
 * button's variant (`primary`/`danger` — the latter because dismissing an
 * unticketed thread leaves no record). Andrew's call: one verb, always
 * primary. The variant branch is gone outright — no flag, no confirm step
 * added to compensate for losing the warning colour.
 *
 * THE TIER BADGE IS A BUTTON IN EVERY STATE (README §4), reopening identity
 * matching via `onChangeIdentity` — unchanged from before this task, just
 * restyled with an icon and the new dashed-border Unresolved treatment (see
 * `tierBadgeStyle` in ./format.ts).
 *
 * Ownership states (README §8 / frame B8), all pre-existing logic, restyled
 * only:
 *   - Unclaimed: primary "Claim", alone, plus an "Unclaimed" pill beside the
 *     tier badge — gates.ts refuses every non-exempt operation to a
 *     non-owner, so a plain tech with no Claim button was a dead end.
 *   - Owned by someone else: secondary "Take over from <name>" plus the peer
 *     pill (role-coloured avatar, filled = typing, outline = reading — never
 *     the signed-in staff member's own beacon reflected back at them).
 *   - Client says done: a second success-soft pill, `cursor-default`, that
 *     never changes the primary action — a signal, not a close.
 *
 * The overflow (`more-horizontal`) icon in the README's B1/B5/B7 frames is
 * deliberately not built: nothing in this header has an overflow menu's
 * worth of content to hide behind it, and every other seam this repaint hit
 * with no wired behaviour (the header's own bell, task 2; the composer's
 * attach icon, this same task) was left out rather than shipped as a control
 * that does nothing when clicked — see Header.tsx's own doc comment for the
 * precedent.
 *
 * Presentational aside from the ticket anchor's own href derivation —
 * `ThreadPane` wires every callback to the operations layer or to
 * `openModal`, per "Do NOT build: the modals themselves — your buttons open
 * them via store state."
 */
import { Avatar, Button, Icon } from '../ui'
import type { Peer } from '../../store'
import type { ThreadSummary } from '@communicator/shared'
import { CHANNEL_ICON, CHANNEL_ICON_VAR } from '../rail/format'
import {
  haloTicketUrl,
  initialsOf,
  isOwnedBySomeoneElse,
  isUnclaimed,
  presenceDotStyle,
  RESOLVE_LABEL,
  sendCodeStyle,
  TIER_BADGE_ICON,
  TIER_BADGE_TEXT,
  tierBadgeStyle,
} from './format'

export interface ThreadHeaderProps {
  thread: ThreadSummary
  peers: Peer[]
  cfg: Record<string, unknown>
  /** The signed-in staff member's real id (`store.me?.id ?? null`, task 27) — see format.ts's `isOwnedBySomeoneElse`. */
  selfId: string | null
  /**
   * Whether more than one SMS line is configured (task 7, multiple SMS
   * lines) — `(store.lines?.length ?? 0) > 1`, computed by `ThreadPane`
   * (this component stays prop-pure, like every other prop here). Gates the
   * "via <label>" chip below: with one line or none, the label never
   * renders even on a thread that carries a `lineLabel` — a single-line
   * deployment's header must stay pixel-identical to before this task.
   */
  showLine: boolean
  onTakeOver: () => void
  onSendCode: () => void
  onAttachTicket: () => void
  onHandOff: () => void
  onCloseOrDismiss: () => void
  /**
   * Re-open identity matching on a thread that already has an answer.
   *
   * The triage strip only appears for an UNRESOLVED tier, which was fine while
   * the only way to be matched was a human choosing. Now that a handle lookup
   * matches automatically, a wrong answer needs a way back — and on a shared
   * main line the automatic answer is a company, not a person, so correcting
   * it is the normal path rather than the exception.
   */
  onChangeIdentity: () => void
}

export function ThreadHeader({
  thread,
  peers,
  cfg,
  selfId,
  showLine,
  onTakeOver,
  onSendCode,
  onAttachTicket,
  onHandOff,
  onCloseOrDismiss,
  onChangeIdentity,
}: ThreadHeaderProps) {
  const closed = thread.state === 'closed'
  // The primary resolve action's wording is admin-configurable (`b.resolveLabel`,
  // runtime-hot) so a deployment can call it "Conversation ended" or anything
  // else; RESOLVE_LABEL ('Resolve') is the fallback when it is unset or blank.
  const resolveLabel =
    typeof cfg['b.resolveLabel'] === 'string' && cfg['b.resolveLabel'].trim()
      ? (cfg['b.resolveLabel'] as string)
      : RESOLVE_LABEL
  const dot = presenceDotStyle(thread.liveness)
  // `null` when the ticket exists but Halo's host is not configured yet —
  // see haloTicketUrl's own doc comment (task-42, bug 1). Never falls back
  // to a link that resolves against this app's own origin.
  const ticketHref = thread.ticket ? haloTicketUrl(cfg, thread.ticket) : null

  // Task 35: never the signed-in staff member's own beacon reflected back as
  // "a peer is here" on the very thread they themselves have open — see
  // RailRow's own `selfId` prop doc comment for the identical rule applied
  // to the rail's badges.
  const typingPeer = peers.find((p) => p.id !== selfId && p.typingThreadId === thread.id)
  const readingPeer = !typingPeer ? peers.find((p) => p.id !== selfId && p.readingThreadId === thread.id) : undefined
  const activePeer = typingPeer ?? readingPeer

  return (
    <div data-testid="thread-header" className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-subtle bg-sidebar px-4 py-[9px]">
      <div className="flex min-w-[220px] flex-[1_1_220px] flex-wrap items-center gap-2">
        <Avatar initials={initialsOf(thread.who)} size={36} />
        <span aria-hidden="true" className={['h-[8px] w-[8px] shrink-0 -ml-1 rounded-full', dot.className].join(' ')} style={dot.style} />
        <span className="font-display text-[16px] font-semibold text-hi">{thread.who}</span>
        <button
          type="button"
          data-testid="thread-header-change-identity"
          onClick={onChangeIdentity}
          title="Change who this conversation is with"
          className="rounded-[20px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-[20px] px-2.5 py-1 text-[11px] font-semibold"
            style={tierBadgeStyle(thread.tier)}
          >
            <Icon name={TIER_BADGE_ICON[thread.tier]} size={12} />
            {TIER_BADGE_TEXT[thread.tier]}
          </span>
        </button>
        {!closed && isUnclaimed(thread.ownerId) && (
          <span
            data-testid="unclaimed-pill"
            className="inline-flex items-center whitespace-nowrap rounded-[20px] bg-warn-subtle px-2.5 py-1 text-[11px] font-semibold text-warn"
          >
            Unclaimed
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 text-[12px] text-mid">
          <Icon name="building-2" size={12} className="flex-none text-lo" />
          {thread.client ?? 'No known company'}
          <span aria-hidden="true" className="text-lo">
            ·
          </span>
          <span className="flex-none" style={{ color: `var(${CHANNEL_ICON_VAR[thread.channel]})` }}>
            <Icon name={CHANNEL_ICON[thread.channel]} size={12} />
          </span>
          {thread.endpoint ?? 'unmapped handle'}
          {showLine && thread.lineLabel && (
            <>
              <span aria-hidden="true" className="text-lo">
                ·
              </span>
              <span className="whitespace-nowrap" data-testid="thread-line-label">
                via {thread.lineLabel}
              </span>
            </>
          )}
        </span>
        {thread.clientResolvedAt && (
          <span
            data-testid="thread-client-resolved-chip"
            title="The client said they're all set. This is a signal, not a close — closing is still yours to do."
            className="inline-flex cursor-default items-center gap-1 whitespace-nowrap rounded-[20px] bg-ok-subtle px-2.5 py-1 text-[11px] font-semibold text-ok"
          >
            <Icon name="check-check" size={12} />
            Client says done
          </span>
        )}
        {activePeer && (
          // Fix round 1 (coordinator review): was `bg-elevated`, the
          // raised-surface token — a visible grey box on Tossker Night (see
          // `channelTileStyle`'s own doc comment in rail/format.ts for the
          // identical mistake made and fixed there). A neutral pill that
          // needs separation from the card takes the sunken/inset token plus
          // a border, not "raised".
          <span
            className="inline-flex items-center gap-1.5 rounded-[20px] border border-subtle bg-sunken px-2 py-0.5 text-[11px] text-mid"
            data-testid="peer-pill"
          >
            <Avatar initials={initialsOf(activePeer.name)} role={activePeer.role} active={!!typingPeer} size={16} />
            {activePeer.name} ({activePeer.role}) is {typingPeer ? 'typing' : 'reading'} here
          </span>
        )}
      </div>

      {/* max-w-full and NO flex-none: with shrink forbidden this row refused
        * to go narrower than all five voice buttons side by side, so its own
        * flex-wrap never engaged and Resolve ran off a phone screen (Andrew,
        * 2026-09-03). Text threads have fewer buttons — which is why texting
        * fit while calls did not. */}
      <div className="ml-auto flex max-w-full flex-wrap items-center gap-2" data-testid="thread-actions">
        {/*
          * Claiming is one obvious click, deliberately.
          *
          * gates.ts refuses every non-exempt operation unless the actor owns
          * the thread — so a tech who cannot claim cannot raise a ticket, note
          * a call, or reply. Until now the header offered "Take over from
          * <name>" and NOTHING for an unowned thread, so an unclaimed
          * conversation was a dead end: "Claim it first" with nothing to click.
          *
          * It hid because dispatch and admins have reach-in and never meet the
          * refusal; it surfaced the first time a plain tech signed in.
          *
          * Primary, not secondary: on an unclaimed thread this is the thing to
          * do, and everything else in the header is refused until it is done.
          */}
        {!closed && isUnclaimed(thread.ownerId) && (
          <Button onClick={onTakeOver} data-testid="claim-button">
            Claim
          </Button>
        )}

        {!closed && isOwnedBySomeoneElse(thread.ownerId, selfId) && (
          <Button variant="secondary" onClick={onTakeOver} data-testid="take-over-button">
            Take over from {thread.owner}
          </Button>
        )}

        {!closed && (
          <button
            type="button"
            data-testid="send-code-button"
            onClick={onSendCode}
            style={sendCodeStyle(thread.tier)}
            className="rounded-control border px-3 py-1.5 text-[13px] font-medium"
          >
            Send a code
          </button>
        )}

        {thread.ticket && ticketHref ? (
          <a
            href={ticketHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ticket-chip"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-1 font-mono text-[12px] font-semibold"
            style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}
          >
            <Icon name="ticket" size={11} />
            {thread.ticket} ↗
          </a>
        ) : thread.ticket ? (
          <span
            data-testid="ticket-chip"
            title="This Halo link can't be built yet — the Halo host isn't configured."
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-1 font-mono text-[12px] font-semibold"
            style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)', opacity: 0.7 }}
          >
            <Icon name="ticket" size={11} />
            {thread.ticket}
          </span>
        ) : closed ? (
          // Fix round 2 (finding 12): was `bg-elevated` — its sibling, the
          // peer pill above, got this exact fix in an earlier round
          // (`border border-subtle bg-sunken`, see that comment); this chip
          // missed it.
          <span className="whitespace-nowrap rounded-[5px] border border-subtle bg-sunken px-2 py-1 text-[11px] text-mid">No ticket</span>
        ) : (
          <button
            type="button"
            onClick={onAttachTicket}
            data-testid="attach-ticket-chip"
            className="whitespace-nowrap rounded-[5px] border border-dashed border-warn bg-warn-subtle px-2 py-1 text-[11px] font-semibold text-warn"
          >
            Attach ticket
          </button>
        )}

        {!closed && (
          <Button variant="secondary" onClick={onHandOff}>
            Hand off
          </Button>
        )}

        {!closed && (
          <Button variant="primary" onClick={onCloseOrDismiss}>
            <Icon name="circle-check" size={14} />
            {resolveLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
